import { __awaiter, __generator, __read } from "tslib";
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useSharedContext } from '@office-start/context-service';
import { useEdgeworthStoreDocumentUpdater } from '@office-start/edgeworth-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { getUrlFileInfo, patchDriveItem, uploadSmallFile, } from '@office-start/graph-service';
import { useLocStrings } from '@office-start/loc-service';
import { logFeature, logUserAction } from '@office-start/telemetry-service';
import { useToastActionCreators } from '@office-start/toast-utility';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState, } from 'react';
import { useEditLinkDialogStore } from '../store/useEditLinkDialogStore';
import { createUrlFile } from '../utilities/createUrlFile';
export var useOpenEditLinkDialog = function () { return useEditLinkDialogStore().openDialog; };
var LazyLinkDialogControl = lazy(function () { return import('@office-start/link-dialog'); });
export function useEditLinkDialog(area) {
    var _this = this;
    var _a, _b;
    var featureGates = useFeatureGates().data;
    var sharedContext = useSharedContext().data;
    var officeComponentContext = useOfficeComponentContext().data;
    var locStrings = useLocStrings().data;
    var onItemMutate = useEdgeworthStoreDocumentUpdater();
    var graphXhrOptions = useMemo(function () {
        return sharedContext && officeComponentContext
            ? {
                graphApiRoot: sharedContext.graphApiRoot,
                correlationId: officeComponentContext.correlationId,
                getAccessToken: officeComponentContext.getAccessToken,
                jwtAuthEnabled: officeComponentContext.jwtAuthEnabled,
                officeApiRoot: officeComponentContext.officeHomeApiRoot,
            }
            : undefined;
    }, [officeComponentContext, sharedContext]);
    var _c = useEditLinkDialogStore(), document = _c.document, isDialogOpen = _c.isDialogOpen, setDialogOpen = _c.setDialogOpen;
    var _d = __read(useState(), 2), defaultNameText = _d[0], setDefaultNameText = _d[1];
    var _f = __read(useState(), 2), defaultAddressText = _f[0], setDefaultAddressText = _f[1];
    var wrappedSetDialogOpen = useCallback(function (isOpen) {
        // Reset the dialog states when it is closed
        if (!isOpen) {
            setDefaultNameText(undefined);
            setDefaultAddressText(undefined);
        }
        setDialogOpen(isOpen);
    }, [setDialogOpen]);
    var _g = useToastActionCreators(), createErrorNotification = _g.createErrorNotification, createSuccessNotification = _g.createSuccessNotification;
    var onUpsertLink = useCallback(function (name, address) { return __awaiter(_this, void 0, void 0, function () {
        var updateAddressResult, updateNameResult, newProps;
        var _this = this;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    logUserAction({
                        eventName: 'Link_Dialog_Edit_Click',
                        id: 'LinkDialogEdit',
                        area: area,
                        result: 'Edit',
                        target: 'Link',
                    });
                    if (!graphXhrOptions) {
                        logFeature({
                            featureName: "Edgeworth" /* KnownFeatureNames.Edgeworth */,
                            eventName: 'EditLink',
                            params: {
                                Error: 'MissingGraphXhrOptions',
                                State: 'Failed',
                            },
                        });
                        return [2 /*return*/, 'error'];
                    }
                    updateAddressResult = undefined;
                    if (!(address !== defaultAddressText)) return [3 /*break*/, 2];
                    return [4 /*yield*/, (function () { return __awaiter(_this, void 0, void 0, function () {
                            var newUrlFile, _e_1;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        newUrlFile = createUrlFile(name, address);
                                        _a.label = 1;
                                    case 1:
                                        _a.trys.push([1, 3, , 4]);
                                        return [4 /*yield*/, uploadSmallFile({
                                                conflictBehavior: 'replace',
                                                driveId: document === null || document === void 0 ? void 0 : document.driveId,
                                                file: newUrlFile,
                                                graphXhrOptions: graphXhrOptions,
                                                itemId: document === null || document === void 0 ? void 0 : document.docId,
                                            })];
                                    case 2:
                                        _a.sent();
                                        return [2 /*return*/, 'success'];
                                    case 3:
                                        _e_1 = _a.sent();
                                        return [2 /*return*/, 'error'];
                                    case 4: return [2 /*return*/];
                                }
                            });
                        }); })()];
                case 1:
                    updateAddressResult = _a.sent();
                    _a.label = 2;
                case 2:
                    updateNameResult = undefined;
                    if (!(name !== defaultNameText)) return [3 /*break*/, 4];
                    return [4 /*yield*/, (function () { return __awaiter(_this, void 0, void 0, function () {
                            var e_1;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        _a.trys.push([0, 2, , 3]);
                                        return [4 /*yield*/, patchDriveItem({
                                                driveId: (document === null || document === void 0 ? void 0 : document.driveId) || '',
                                                itemId: (document === null || document === void 0 ? void 0 : document.docId) || '',
                                                graphXhrOptions: graphXhrOptions,
                                                patchedItem: {
                                                    name: "".concat(name, ".url"),
                                                },
                                            })];
                                    case 1:
                                        _a.sent();
                                        return [2 /*return*/, 'success'];
                                    case 2:
                                        e_1 = _a.sent();
                                        if ((e_1 === null || e_1 === void 0 ? void 0 : e_1.clientError) === 'nameAlreadyExists') {
                                            return [2 /*return*/, 'nameExists'];
                                        }
                                        return [2 /*return*/, 'error'];
                                    case 3: return [2 /*return*/];
                                }
                            });
                        }); })()];
                case 3:
                    updateNameResult = _a.sent();
                    _a.label = 4;
                case 4:
                    if (updateNameResult === 'success') {
                        newProps = document
                            ? {
                                title: name,
                                lastActionTime: new Date(),
                            }
                            : undefined;
                        setDefaultNameText(name);
                        if (newProps && document) {
                            onItemMutate(document.id, newProps);
                        }
                    }
                    if (updateAddressResult === 'success') {
                        setDefaultAddressText(address);
                    }
                    if ((updateAddressResult && updateAddressResult !== 'success') ||
                        (updateNameResult && updateNameResult !== 'success')) {
                        logFeature({
                            featureName: "Edgeworth" /* KnownFeatureNames.Edgeworth */,
                            eventName: 'EditLink',
                            params: {
                                Error: "UpdateAddressResult:".concat(updateAddressResult, ",UpdateNameResult:").concat(updateNameResult),
                                State: 'Failed',
                            },
                        });
                        createErrorNotification((locStrings === null || locStrings === void 0 ? void 0 : locStrings.linksResources.linkEditFailureMessage) || '');
                        return [2 /*return*/, 'error'];
                    }
                    logFeature({
                        featureName: "Edgeworth" /* KnownFeatureNames.Edgeworth */,
                        eventName: 'EditLink',
                        params: {
                            State: 'Succeeded',
                        },
                    });
                    createSuccessNotification((locStrings === null || locStrings === void 0 ? void 0 : locStrings.linksResources.linkEditSuccessMessage) || '');
                    return [2 /*return*/, 'success'];
            }
        });
    }); }, [
        area,
        createErrorNotification,
        createSuccessNotification,
        defaultAddressText,
        defaultNameText,
        document,
        graphXhrOptions,
        locStrings === null || locStrings === void 0 ? void 0 : locStrings.linksResources.linkEditFailureMessage,
        locStrings === null || locStrings === void 0 ? void 0 : locStrings.linksResources.linkEditSuccessMessage,
        onItemMutate,
    ]);
    useEffect(function () {
        var isMounted = true;
        var getFile = function () { return __awaiter(_this, void 0, void 0, function () {
            var fileInfo;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(isDialogOpen &&
                            graphXhrOptions &&
                            document &&
                            document.driveId &&
                            document.docId)) return [3 /*break*/, 2];
                        return [4 /*yield*/, getUrlFileInfo(document.driveId, document.docId, graphXhrOptions)];
                    case 1:
                        fileInfo = _a.sent();
                        if (fileInfo && isMounted) {
                            setDefaultNameText(document.title);
                            setDefaultAddressText(fileInfo.address);
                        }
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        }); };
        getFile();
        return function () {
            isMounted = false;
        };
    }, [document, graphXhrOptions, isDialogOpen]);
    return ((_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.edgeworthConfigGates) === null || _b === void 0 ? void 0 : _b.edgeworthConfigLinksEnabled) &&
        defaultNameText &&
        defaultAddressText &&
        document &&
        locStrings ? (React.createElement(Suspense, { fallback: null },
        React.createElement(LazyLinkDialogControl, { defaultNameText: defaultNameText, defaultAddressText: defaultAddressText, locStrings: {
                addressLabel: locStrings.linksResources.addressLabelText,
                cancelButtonText: locStrings.linksResources.cancelButtonText,
                invalidAddressErrorMessage: locStrings.linksResources.invalidAddressErrorText,
                invalidNameErrorFormatMessage: locStrings.linksResources.invalidNameErrorFormatText,
                nameExistsErrorMessage: locStrings.linksResources.nameExistsErrorText,
                nameLabel: locStrings.linksResources.nameLabelText,
                nameRequiredErrorMessage: locStrings.linksResources.nameRequiredErrorText,
                primaryButtonText: locStrings.linksResources.editButtonText,
                title: locStrings.linksResources.editLinkTitle,
            }, onUpsertLink: onUpsertLink, openDialog: isDialogOpen, setOpenDialog: wrappedSetDialogOpen }))) : undefined;
}
//# sourceMappingURL=useEditLinkDialog.js.map